import { SHARE_SEARCH_URL_PARAM } from '@aibees/elephant/context/search-params-url-sync';
import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import {
  useSearchParamsStore,
  type SearchTableScope,
} from '@aibees/elephant/store/search-params-store';
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { useReadShareablePresetApi } from '@aibees/shared/api';
import { useNotify } from '@aibees/shared/hooks';
import { objSanitizeNilValues } from '@aibees/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reads the one-shot `?search-id=<presetId>` link param, fetches that shared
 * `SHARE_SEARCH` preset, replaces the current scope's params with its value, then
 * strips the param from the URL. The normal store <-> URL sync then expands the
 * store into the full filter query string.
 *
 * The share id is LATCHED from the initial URL: because `search-id` is excluded
 * from the filter sync (`omitNonFilterParams`), the store <-> URL sync treats the
 * URL as filter-empty and rewrites it (dropping `search-id`) before our async
 * fetch resolves. Reading it from live `searchParams` would therefore go null
 * mid-flight; latching keeps it stable until the preset is applied.
 */
export function useApplySharedSearch() {
  const scope = useSearchTableScope();
  const [, setSearchParams] = useSearchParams();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const resetHistoryTo = useSearchHistoryStore.useResetTo();
  const notify = useNotify();

  const [shareId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get(SHARE_SEARCH_URL_PARAM)
  );
  const appliedRef = useRef(false);

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';
  const shouldFetch = !!shareId && hasScope && !appliedRef.current;

  const { data, isError, isSuccess } = useReadShareablePresetApi<
    Partial<SearchBrowseParams>
  >(shareId ?? undefined, { enabled: shouldFetch });

  const stripShareId = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SHARE_SEARCH_URL_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!shareId || !hasScope || appliedRef.current) return;

    if (isError) {
      appliedRef.current = true;
      notify.error('This shared search link could not be loaded.');
      stripShareId();
      return;
    }

    if (isSuccess) {
      appliedRef.current = true;
      const applied = (data?.value ?? {}) as Partial<SearchBrowseParams>;
      replaceParamsForScope(scope as SearchTableScope, applied);
      // Seed the applied state as the undo/redo baseline so the header "undo"
      // returns to the shared search (and can't drop below it into empty filters).
      resetHistoryTo(
        scope as SearchTableScope,
        objSanitizeNilValues(applied) as Record<string, string>
      );
      stripShareId();
    }
  }, [
    shareId,
    hasScope,
    scope,
    data,
    isError,
    isSuccess,
    replaceParamsForScope,
    resetHistoryTo,
    notify,
    stripShareId,
  ]);
}
